import React, { useState, useEffect, useMemo } from 'react';
import {
  TimeEntry,
  ChargeCodesDict,
  ProjectTicket,
  PatchOperation,
} from '../types/api.types';
import { apiService, BadRequestError } from '../services/apiService';
import './ChargeCodeEditModal.css';

type ChargeType = 'ChargeCode' | 'ProjectTicket';

interface ChargeCodeEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void | Promise<void>;
  entry: TimeEntry;
  chargeCodes: ChargeCodesDict;
}

const ChargeCodeEditModal: React.FC<ChargeCodeEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  entry,
  chargeCodes,
}) => {
  // Form state
  const [chargeType, setChargeType] = useState<ChargeType>('ChargeCode');
  const [selectedChargeCodeId, setSelectedChargeCodeId] = useState<number>(0);
  const [projectNumber, setProjectNumber] = useState('');
  const [projectTickets, setProjectTickets] = useState<ProjectTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<ProjectTicket | null>(null);
  const [chargeCodeFilter, setChargeCodeFilter] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      setChargeType(entry.chargeToType);
      setError(null);

      if (entry.chargeToType === 'ChargeCode') {
        setSelectedChargeCodeId(entry.chargeToId);
        setProjectNumber('');
        setProjectTickets([]);
        setSelectedTicket(null);
      } else {
        setSelectedChargeCodeId(0);
        const projectId = entry.project?.id?.toString() || '';
        setProjectNumber(projectId);

        // Auto-search and select the current ticket
        if (projectId) {
          setIsSearching(true);
          apiService
            .searchProjectTickets(projectId)
            .then((tickets) => {
              setProjectTickets(tickets);
              const matchingTicket = tickets.find((t) => t.id === entry.chargeToId);
              if (matchingTicket) {
                setSelectedTicket(matchingTicket);
              } else {
                setSelectedTicket({
                  id: entry.chargeToId,
                  summary: entry.ticket?.summary || '',
                  company: entry.company,
                  project: entry.project,
                });
              }
            })
            .catch(() => {
              setSelectedTicket({
                id: entry.chargeToId,
                summary: entry.ticket?.summary || '',
                company: entry.company,
                project: entry.project,
              });
            })
            .finally(() => {
              setIsSearching(false);
            });
        } else {
          setSelectedTicket({
            id: entry.chargeToId,
            summary: entry.ticket?.summary || '',
            company: entry.company,
            project: entry.project,
          });
        }
      }

      setChargeCodeFilter('');
    }
  }, [isOpen, entry]);

  // Filtered charge codes
  const filteredChargeCodes = useMemo(() => {
    const codes = Object.values(chargeCodes);
    if (!chargeCodeFilter) return codes;
    const filter = chargeCodeFilter.toLowerCase();
    return codes.filter(
      (code) =>
        code.name.toLowerCase().includes(filter) ||
        code.company?.name?.toLowerCase().includes(filter)
    );
  }, [chargeCodes, chargeCodeFilter]);

  // Search for project tickets
  const handleSearchTickets = async () => {
    if (!projectNumber.trim()) return;
    setIsSearching(true);
    setError(null);
    try {
      const tickets = await apiService.searchProjectTickets(projectNumber.trim());
      setProjectTickets(tickets);
      if (tickets.length === 0) {
        setError('No tickets found for this project');
      }
    } catch (err) {
      console.error('Error searching tickets:', err);
      setError('Failed to search for tickets');
    } finally {
      setIsSearching(false);
    }
  };

  // Check if the selection has actually changed
  const hasChanges = useMemo(() => {
    if (chargeType !== entry.chargeToType) return true;
    if (chargeType === 'ChargeCode') {
      return selectedChargeCodeId !== entry.chargeToId;
    }
    return selectedTicket?.id !== entry.chargeToId;
  }, [chargeType, selectedChargeCodeId, selectedTicket, entry]);

  // Validate and save
  const handleSave = async () => {
    setError(null);

    if (chargeType === 'ChargeCode' && !selectedChargeCodeId) {
      setError('Please select a charge code');
      return;
    }
    if (chargeType === 'ProjectTicket' && !selectedTicket) {
      setError('Please select a project ticket');
      return;
    }

    if (!hasChanges) {
      onClose();
      return;
    }

    setIsSaving(true);

    try {
      const chargeToId =
        chargeType === 'ChargeCode' ? selectedChargeCodeId : selectedTicket!.id;

      const operations: PatchOperation[] = [
        { op: 'replace', path: 'chargeToId', value: chargeToId },
        { op: 'replace', path: 'chargeToType', value: chargeType },
      ];

      await apiService.updateTimeEntry(entry.id, operations);
      await onSave();
      onClose();
    } catch (err) {
      console.error('Error updating charge code:', err);
      if (err instanceof BadRequestError) {
        alert(err.message);
      } else {
        const message =
          err instanceof Error ? err.message : 'Failed to update charge code';
        setError(message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content charge-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Change Charge Code</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label>Charge Type</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="editChargeType"
                  value="ChargeCode"
                  checked={chargeType === 'ChargeCode'}
                  onChange={() => {
                    setChargeType('ChargeCode');
                    setSelectedTicket(null);
                    setProjectTickets([]);
                    setProjectNumber('');
                  }}
                />
                Charge Code
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="editChargeType"
                  value="ProjectTicket"
                  checked={chargeType === 'ProjectTicket'}
                  onChange={() => {
                    setChargeType('ProjectTicket');
                    setSelectedChargeCodeId(0);
                  }}
                />
                Project
              </label>
            </div>
          </div>

          {chargeType === 'ChargeCode' && (
            <div className="form-group">
              <label>Charge Code</label>
              <input
                type="text"
                placeholder="Filter charge codes..."
                value={chargeCodeFilter}
                onChange={(e) => setChargeCodeFilter(e.target.value)}
                className="form-input filter-input"
              />
              <select
                value={selectedChargeCodeId}
                onChange={(e) =>
                  setSelectedChargeCodeId(parseInt(e.target.value, 10))
                }
                className="form-select"
                size={5}
              >
                <option value={0}>-- Select Charge Code --</option>
                {filteredChargeCodes.map((code) => (
                  <option key={code.id} value={code.id}>
                    {code.company?.name || 'Unknown'} / {code.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {chargeType === 'ProjectTicket' && (
            <div className="form-group">
              <label>Project Number</label>
              <div className="search-row">
                <input
                  type="text"
                  placeholder="Enter project ID"
                  value={projectNumber}
                  onChange={(e) => setProjectNumber(e.target.value)}
                  className="form-input"
                />
                <button
                  className="btn btn-secondary"
                  onClick={handleSearchTickets}
                  disabled={isSearching}
                >
                  {isSearching ? 'Searching...' : 'Search'}
                </button>
              </div>
              {projectTickets.length > 0 && (
                <select
                  value={selectedTicket?.id ?? 0}
                  onChange={(e) => {
                    const ticket = projectTickets.find(
                      (t) => t.id === parseInt(e.target.value, 10)
                    );
                    setSelectedTicket(ticket || null);
                  }}
                  className="form-select"
                  size={5}
                >
                  <option value={0}>-- Select Ticket --</option>
                  {projectTickets.map((ticket) => (
                    <option key={ticket.id} value={ticket.id}>
                      {ticket.company?.name} / {ticket.project?.name} /{' '}
                      {ticket.summary}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
          >
            {isSaving ? 'Saving...' : 'Update Charge Code'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChargeCodeEditModal;
